import type { UseFloatingOptions } from '@floating-ui/react';
import {
  autoUpdate,
  flip,
  shift,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useState } from 'react';

interface UseDropdownOpts {
  /*
   * Update menu position on every animation frame, this is expensive so only
   * use when needed (e.g. inside timeline items)
   */
  aggressiveUpdate?: boolean;
  closeOnContentClick?: boolean;
  closeOnOutsideClick?: boolean;
  floatProps?: Partial<UseFloatingOptions>;
}

/* Detect clicks inside floatingEl and call setVisibility if closeOnContentClick === true */
export function setupCloseOnContentClick(
  floatingEl: HTMLElement,
  closeOnContentClick: boolean,
  setVisibility: (isVisible: boolean) => void,
) {
  const documentEl = document;

  const handleOutsideClick = (event: MouseEvent) => {
    const clickedInsideFloating = floatingEl.contains(event.target as Element);

    if (closeOnContentClick && clickedInsideFloating) {
      setVisibility(false);
    }
  };

  documentEl.addEventListener('click', handleOutsideClick);
  documentEl.addEventListener('contextmenu', handleOutsideClick, {
    capture: true,
  });

  return () => {
    documentEl.removeEventListener('click', handleOutsideClick);
    documentEl.removeEventListener('contextmenu', handleOutsideClick);
  };
}

function useDropdown(opts?: UseDropdownOpts): {
  getTargetProps: (
    userProps?: React.HTMLProps<Element>,
  ) => Record<string, unknown>;
  getMenuProps: (
    userProps?: React.HTMLProps<HTMLElement>,
  ) => Record<string, unknown>;
  setTargetRef: (node: HTMLElement | null) => void;
  setMenuRef: (node: HTMLElement | null) => void;
  styles: { zIndex: number };
  isVisible: boolean;
  setVisibility: (value: ((prevState: boolean) => boolean) | boolean) => void;
} {
  const aggressiveUpdate = opts?.aggressiveUpdate ?? false;
  const closeOnContentClick = opts?.closeOnContentClick ?? true;
  const closeOnOutsideClick = opts?.closeOnOutsideClick ?? true;
  const floatProps = opts?.floatProps ?? {};
  const { placement, ...restFloatProps } = floatProps;

  const [isVisible, setVisibility] = useState<boolean>(false);

  const { context, refs, strategy, x, y } = useFloating({
    open: isVisible,
    onOpenChange: setVisibility,
    placement: placement ? placement : 'bottom-start',
    whileElementsMounted: (reference, floating, update) => {
      // Remember to cleanup everything happening inside this!
      const cleanUpAutoUpdate = autoUpdate(reference, floating, update, {
        animationFrame: aggressiveUpdate,
        layoutShift: aggressiveUpdate,
        elementResize: true,
        ancestorResize: true,
        ancestorScroll: true,
      });

      const cleanUpCloseOnContentClick = setupCloseOnContentClick(
        floating,
        closeOnContentClick,
        setVisibility,
      );

      return () => {
        cleanUpAutoUpdate();
        cleanUpCloseOnContentClick();
      };
    },
    middleware: [offset(2), flip(), shift()],
    ...restFloatProps,
  });

  const { getReferenceProps: getTargetProps, getFloatingProps: getMenuProps } =
    useInteractions([
      useDismiss(context, {
        outsidePress: closeOnOutsideClick,
      }),
    ]);

  const styles = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    zIndex: 1000,
  };

  return {
    isVisible,
    setVisibility,
    getMenuProps,
    getTargetProps,
    setMenuRef: refs.setFloating,
    setTargetRef: refs.setReference,
    styles,
  };
}

export default useDropdown;
